// Dashboard → EWS (Early Warning System): the answer's shape, how each figure is
// written, and the Excel sheet of the table as shown. No React in here.
import type { TableSheet } from '../../utils/tableXlsx';

/** The figures, in the order the server sends and the table draws them. */
export type EwsColumn = 'redFlags' | 'incentive' | 'unplanned' | 'hours' | 'score' | 'tickets' | 'mood';

export type EwsPriority = 'red' | 'amber';

/** Where the conversation with someone flagged stands. */
export interface EwsContact {
  state: 'done' | 'due' | 'overdue';
  dueAt: string;
  hoursLeft: number | null;
}

export interface EwsRow {
  code: string;
  name: string;
  /** Their last store on record (a leaver's too) and where it is; '' when not on record. */
  store: string;
  trade?: string;
  category?: string;
  city?: string;
  state?: string;
  /** One value per month of the window, oldest first; null = no figure that month. */
  values: Partial<Record<EwsColumn, Array<number | null>>>;
  /** Last working day of someone who has left, 'YYYY-MM-DD'; null or absent for someone working. */
  leftOn?: string | null;
  /** Date of joining, 'YYYY-MM-DD'; null when 1Office has none. */
  joinedOn?: string | null;
  /** AON: joining date to today, or to the last working day for a leaver. Days, and "2y 3m". */
  aonDays?: number | null;
  aonLabel?: string | null;
  /** The Assistant Manager and Trade Head at or above them. */
  amCode?: string | null;
  amName?: string | null;
  thCode?: string | null;
  thName?: string | null;
  /** Days with 9 hours or more, per month of the window; null where the month has no hours. */
  fullDays?: Array<number | null>;
  /** Red flags still standing in the window's last month (cancelled on appeal left out). */
  standingFlags?: number | null;
  /** The warning signs firing for the window's last month. */
  signals?: EwsColumn[];
  priority?: EwsPriority | null;
  remarkCount?: number;
  lastRemarkAt?: string | null;
  /** Only for someone flagged in the latest finished month. */
  contact?: EwsContact | null;
}

/**
 * Who EWS lists: those working today, those who left during the chosen months, or both —
 * everyone who worked in them, those who left after them included.
 */
export type EwsEmployees = 'active' | 'left' | 'all';

export const EWS_EMPLOYEE_OPTIONS: Array<{ value: EwsEmployees; label: string }> = [
  { value: 'active', label: 'Working today' },
  { value: 'left', label: 'Left' },
  { value: 'all', label: 'Both' },
];

export interface EwsData {
  /** 'YYYY-MM', oldest first. */
  months: string[];
  endMonth: string;
  /** The columns this project shows: those it has data for, less those its admins switched off. */
  columns: EwsColumn[];
  /** Every parameter the project has data for, and whether it is on. */
  columnChoices?: Array<{ key: EwsColumn; on: boolean }>;
  /** The viewer may switch parameters on and off for the project (its admins). */
  canChooseColumns?: boolean;
  /** Shown columns that could not be read this time: dashes, with a line saying so. */
  failed: EwsColumn[];
  /** Shown columns still being read in the background: asked again until they land. */
  pending: EwsColumn[];
  /** No joining date could be read: a month before someone joined may show 0. */
  joinDatesMissing: boolean;
  /**
   * Which Helpdesk tickets the Tickets column counts: 'all' channels, or only those raised
   * from the 'app' chat (Helpdesk's report as first shipped). null when not read this time.
   */
  ticketsChannels?: 'all' | 'app' | null;
  /** Names could not be read: codes are shown instead. */
  partial: boolean;
  /** The stores could not be read: store and location are blank. */
  storesMissing?: boolean;
  /** Whether the project has these roles, and whether a reporting line could not be read. */
  managerTiers?: { available: boolean; partial: boolean };
  /** The viewer may read and add remarks (EWS is ticked for them in this project). */
  remarksAllowed?: boolean;
  /** Remarks could not be read: the counts and the timers are not shown as if nobody had spoken. */
  remarksMissing?: boolean;
  rows: EwsRow[];
}

export const EWS_COLUMN_LABEL: Record<EwsColumn, string> = {
  redFlags: 'Red flags',
  incentive: 'Incentive ₹',
  unplanned: 'Unplanned leave',
  hours: 'Hours / day',
  score: 'Score',
  tickets: 'Tickets',
  mood: 'Mood',
};

/** On hover over a column's name: what the number is. */
export const EWS_COLUMN_HINT: Record<EwsColumn, string> = {
  redFlags: 'Red flags raised for the month, including those cancelled after an appeal. A warning at 3 or more still standing',
  incentive: 'What Sales & Targets › Incentive works out for the month. A warning at 20% or more below the month before, or ₹0 while on a plan',
  unplanned: 'Days marked Absent with no approved leave. A warning at more than 2 in the month',
  hours: 'Average time from sign-in to sign-out, over the days with both, and the days that reached 9 hours. A warning when the average is up by 1 hour or more and at 9 hours or more',
  score: 'The Employee Score stored for the month. A warning at 20% or more below the month before',
  tickets: 'Helpdesk tickets the employee raised (app chat, WhatsApp, email), by the month raised. A warning when up by 3 or more on the month before',
  mood: 'Average of the daily mood check-ins: 1 very unhappy, 5 very happy. A warning when down by 0.5 or more on the month before',
};

/**
 * A column's name as the table prints it. Tickets is "App tickets" only when Helpdesk says
 * it counted the app chat alone (its report as first shipped): a column called just
 * "Tickets" would then read as "no tickets" for months people were raising them on
 * WhatsApp. Since Helpdesk #319 it counts every channel.
 */
export const ewsColumnLabel = (col: EwsColumn, data: Pick<EwsData, 'ticketsChannels'>): string =>
  col === 'tickets' && data.ticketsChannels === 'app' ? 'App tickets' : EWS_COLUMN_LABEL[col];

/** A column's hover line, as ewsColumnLabel names it. */
export const ewsColumnHint = (col: EwsColumn, data: Pick<EwsData, 'ticketsChannels'>): string =>
  col === 'tickets' && data.ticketsChannels === 'app'
    ? 'Tickets raised from the app’s Helpdesk chat, by the month raised. WhatsApp and email tickets are added once Helpdesk includes them'
    : EWS_COLUMN_HINT[col];

const rupees = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

/** A figure as the table writes it. null is a dash. */
export const formatEws = (col: EwsColumn, value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—';
  switch (col) {
    case 'mood':
    case 'hours':
    case 'score':
      return value.toFixed(1);
    case 'incentive':
      return rupees.format(Math.round(value));
    default:
      return String(Math.round(value));
  }
};

// Written out rather than asked of the browser: en-IN says "Sept", and the column must
// read the same in every browser and in the Excel file.
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'Jul' for '2026-07'. */
export const shortMonth = (ym: string): string => MONTH_SHORT[Number(ym.slice(5, 7)) - 1] ?? ym;

// ── The warning signs ────────────────────────────────────────────────────────

export const priorityLabel = (priority: EwsPriority): string => (priority === 'red' ? 'Red' : 'Amber');

/** Why someone is flagged, in two or three words. */
export const priorityWhy = (row: Pick<EwsRow, 'signals'>): string => {
  const signs = row.signals ?? [];
  if (signs.length === 0) return '';
  if (signs.length < 3 && signs.includes('redFlags')) return 'Recurring flags';
  return `${signs.length} signal${signs.length === 1 ? '' : 's'}`;
};

/** The number behind a highlighted figure, for its hover line. */
export const signalReason = (col: EwsColumn, row: EwsRow, months: readonly string[]): string => {
  const last = months.length - 1;
  const now = row.values[col]?.[last];
  const before = row.values[col]?.[last - 1];
  const thisMonth = shortMonth(months[last]);
  const lastMonth = last > 0 ? shortMonth(months[last - 1]) : '';
  const down = () => (before ? Math.round(((before - (now ?? 0)) / before) * 100) : 0);
  switch (col) {
    case 'redFlags':
      return `${row.standingFlags ?? now} red flags still standing in ${thisMonth}`;
    case 'incentive':
      return now === 0 ? `₹0 earned in ${thisMonth} while on a plan` : `Down ${down()}% from ${lastMonth}`;
    case 'unplanned':
      return `${now} days Absent with no approved leave in ${thisMonth}`;
    case 'hours':
      return `Up ${((now ?? 0) - (before ?? 0)).toFixed(1)} hours a day from ${lastMonth}`;
    case 'score':
      return `Down ${down()}% from ${lastMonth}`;
    case 'tickets':
      return `Up ${(now ?? 0) - (before ?? 0)} from ${lastMonth}`;
    case 'mood':
      return `Down ${((before ?? 0) - (now ?? 0)).toFixed(1)} from ${lastMonth}`;
  }
};

/** 'Overdue' or 'Due in 41h' while someone flagged has not been spoken to; '' otherwise. */
export const contactLabel = (contact: EwsContact | null | undefined): string => {
  if (!contact || contact.state === 'done') return '';
  return contact.state === 'overdue' ? 'Overdue' : `Due in ${contact.hoursLeft}h`;
};

/** Flagged, and nobody has written down a conversation since. */
export const needsConversation = (row: Pick<EwsRow, 'priority' | 'contact' | 'remarkCount'>): boolean => {
  if (!row.priority) return false;
  return row.contact ? row.contact.state !== 'done' : !row.remarkCount;
};

/**
 * Columns whose latest two months hold almost no figures, so the sign cannot fire for
 * anyone yet (a month's Score held for its uploads, Incentive before any sales are in).
 */
export const columnsNotCompared = (data: Pick<EwsData, 'columns' | 'months' | 'rows' | 'failed' | 'pending'>): EwsColumn[] => {
  const last = data.months.length - 1;
  const working = data.rows.filter((r) => !r.leftOn);
  if (last < 1 || working.length === 0) return [];
  const comparable: EwsColumn[] = ['incentive', 'hours', 'score', 'mood'];
  return data.columns.filter((col) => {
    if (!comparable.includes(col) || data.failed.includes(col) || (data.pending ?? []).includes(col)) return false;
    const filled = (i: number) => working.filter((r) => r.values[col]?.[i] != null).length;
    return Math.min(filled(last), filled(last - 1)) < working.length * 0.1;
  });
};

export type EwsManagerTier = 'am' | 'th';

export interface EwsManagerRow {
  /** null: the people with no Assistant Manager or Trade Head. */
  code: string | null;
  name: string;
  team: number;
  red: number;
  amber: number;
  noConversation: number;
}

export const managerTierLabel = (tier: EwsManagerTier): string => (tier === 'am' ? 'Assistant Manager' : 'Trade Head');

/** This person's manager at that level. */
export const managerCodeOf = (row: EwsRow, tier: EwsManagerTier): string | null =>
  (tier === 'am' ? row.amCode : row.thCode) ?? null;

/** One row per Assistant Manager or Trade Head: most Red first, the people with none last. */
export const managerRows = (rows: readonly EwsRow[], tier: EwsManagerTier): EwsManagerRow[] => {
  const by = new Map<string | null, EwsManagerRow>();
  for (const r of rows) {
    const code = managerCodeOf(r, tier);
    const name = (tier === 'am' ? r.amName : r.thName) || code || `No ${managerTierLabel(tier)}`;
    const m = by.get(code) ?? { code, name, team: 0, red: 0, amber: 0, noConversation: 0 };
    m.team += 1;
    if (r.priority === 'red') m.red += 1;
    if (r.priority === 'amber') m.amber += 1;
    if (needsConversation(r)) m.noConversation += 1;
    by.set(code, m);
  }
  return [...by.values()].sort((a, b) =>
    Number(a.code === null) - Number(b.code === null) || b.red - a.red || b.amber - a.amber || a.name.localeCompare(b.name));
};

// ── Remarks ──────────────────────────────────────────────────────────────────

export interface EwsRemark {
  id: number;
  employeeCode: string;
  text: string;
  byCode: string;
  byName: string | null;
  at: string;
}

/** The longest remark the server keeps. */
export const EWS_REMARK_MAX_CHARS = 2000;

const IST = 'Asia/Kolkata';

/** '28 Oct 2026, 4:05 pm IST': every reader sees India time, wherever their browser is. */
export const remarkWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const part = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-GB', { timeZone: IST, ...o }).format(d);
  const month = MONTH_SHORT[Number(part({ month: 'numeric' })) - 1];
  const clock = new Intl.DateTimeFormat('en-US', { timeZone: IST, hour: 'numeric', minute: '2-digit', hour12: true }).format(d).toLowerCase();
  return `${Number(part({ day: 'numeric' }))} ${month} ${part({ year: 'numeric' })}, ${clock} IST`;
};

/** '28 Oct', in India time. */
export const remarkDay = (iso: string | null | undefined): string => (iso ? remarkWhen(iso).split(' ').slice(0, 2).join(' ') : '');

/** 'Kiran Nair (900501)', or the code alone. */
export const remarkBy = (remark: Pick<EwsRemark, 'byCode' | 'byName'>): string =>
  (remark.byName ? `${remark.byName} (${remark.byCode})` : remark.byCode);

/** Search: a name or an employee code, any part of it, any letter case. */
export const matchesSearch = (row: EwsRow, text: string): boolean => {
  const q = text.trim().toLowerCase();
  if (!q) return true;
  return row.name.toLowerCase().includes(q) || row.code.toLowerCase().includes(q);
};

/** 'Pune, Maharashtra' — the parts that are known. */
export const placeLabel = (row: Pick<EwsRow, 'city' | 'state'>): string =>
  [row.city, row.state].map((p) => (p ?? '').trim()).filter(Boolean).join(', ');

/** 'MBR · Premium' — the parts that are known. */
export const storeKindLabel = (row: Pick<EwsRow, 'trade' | 'category'>): string =>
  [row.trade, row.category].map((p) => (p ?? '').trim()).filter(Boolean).join(' · ');

/** 'Left 12 Sep 2026' for a last working day. */
export const leftLabel = (leftOn: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(leftOn ?? ''));
  return m ? `Left ${Number(m[3])} ${MONTH_SHORT[Number(m[2]) - 1] ?? m[2]} ${m[1]}` : '';
};

/** 'Joined 15 Apr 2024', or '' when the date is not known. */
export const joinedLabel = (joinedOn: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(joinedOn ?? ''));
  return m ? `Joined ${Number(m[3])} ${MONTH_SHORT[Number(m[2]) - 1] ?? m[2]} ${m[1]}` : '';
};

/** 'Oct 2026' for '2026-10'. */
export const monthLabel = (ym: string): string => `${shortMonth(ym)} ${ym.slice(0, 4)}`;

/** The table as shown, for the month picked: the rows in their order and every column, as one sheet. */
export const ewsSheet = (
  data: Pick<EwsData, 'months' | 'columns' | 'ticketsChannels' | 'remarksAllowed' | 'remarksMissing' | 'managerTiers'>,
  rows: readonly EwsRow[],
  regionOf: (state?: string | null) => string | undefined = () => undefined,
): TableSheet => {
  const anyLeft = rows.some((r) => r.leftOn);
  // Remarks that could not be read are left out, not written as "nobody has spoken to them".
  const remarks = data.remarksAllowed === true && !data.remarksMissing;
  const managers = data.managerTiers?.available === true;
  const headers = [
    'Employee', 'Code', 'Priority', 'Why flagged',
    ...(remarks ? ['Contact', 'Remarks', 'Last remark on'] : []),
    ...(managers ? ['Assistant Manager', 'Trade Head'] : []),
    'Store', 'Trade', 'Category', 'City', 'State', 'Region', 'Joined on', 'AON', 'AON (days)',
    ...(anyLeft ? ['Left on'] : []),
  ];
  // The month picked: the month before it is read only to compare.
  const last = data.months.length - 1;
  const shown = data.months[last] ? monthLabel(data.months[last]) : '';
  for (const col of data.columns) {
    headers.push(`${ewsColumnLabel(col, data)} ${shown}`);
    if (col === 'hours') headers.push(`Days 9h+ ${shown}`);
  }
  return {
    name: 'EWS',
    headers,
    rows: rows.map((r) => [
      r.name,
      r.code,
      r.priority ? priorityLabel(r.priority) : '',
      (r.signals ?? []).map((col) => EWS_COLUMN_LABEL[col]).join(', '),
      ...(remarks ? [contactLabel(r.contact), r.remarkCount ?? 0, remarkDay(r.lastRemarkAt)] : []),
      ...(managers ? [r.amName ?? '', r.thName ?? ''] : []),
      r.store,
      r.trade ?? '',
      r.category ?? '',
      r.city ?? '',
      r.state ?? '',
      (r.state && regionOf(r.state)) || '',
      r.joinedOn ?? '',
      r.aonLabel ?? '',
      r.aonDays ?? null,
      ...(anyLeft ? [r.leftOn ?? ''] : []),
      ...data.columns.flatMap((col) => [
        r.values[col]?.[last] ?? null,
        ...(col === 'hours' ? [r.fullDays?.[last] ?? null] : []),
      ]),
    ]),
  };
};

/** The By Assistant Manager or By Trade Head table as shown. */
export const managerSheet = (tier: EwsManagerTier, rows: readonly EwsManagerRow[], conversations: boolean): TableSheet => ({
  name: `By ${managerTierLabel(tier)}`,
  headers: [managerTierLabel(tier), 'Team', 'Red', 'Amber', ...(conversations ? ['No conversation yet'] : [])],
  rows: rows.map((m) => [m.name, m.team, m.red, m.amber, ...(conversations ? [m.noConversation] : [])]),
});

/** Every remark about the people in the file, each on its own row, oldest first. */
export const remarksSheet = (remarks: readonly EwsRemark[], rows: readonly EwsRow[]): TableSheet => {
  const nameOf = new Map(rows.map((r) => [r.code, r.name]));
  return {
    name: 'Remarks',
    headers: ['Employee', 'Code', 'Remark', 'Written by', 'Written on'],
    rows: remarks
      .filter((m) => nameOf.has(m.employeeCode))
      .map((m) => [nameOf.get(m.employeeCode), m.employeeCode, m.text, remarkBy(m), remarkWhen(m.at)]),
  };
};

// ── A ticket count, opened ───────────────────────────────────────────────────

export interface EwsTicket {
  ticketNumber: string;
  subject: string;
  status: string;
  /** app | whatsapp | email | suite; null when Helpdesk says none. */
  channel?: string | null;
  /** IST wall time with its offset, as Helpdesk writes it: 'YYYY-MM-DDTHH:MM:SS+05:30'. */
  createdAt: string | null;
  updatedAt: string | null;
}

export interface EwsTicketMessage {
  id: number;
  from: 'employee' | 'other' | 'assistant' | 'support_team' | 'system' | null;
  text: string;
  createdAt: string | null;
  attachments: string[];
}

export interface EwsConversation {
  ticket: EwsTicket & { empCode: string | null };
  messages: EwsTicketMessage[];
  hasMore: boolean;
  /** Where the next part starts (Helpdesk's own word: on WhatsApp it skips emails the employee never got). */
  nextAfter?: number;
}

const STATUS: Record<string, { label: string; tone: string }> = {
  OPEN: { label: 'Open', tone: 'bg-amber-50 text-amber-700' },
  WORK_IN_PROGRESS: { label: 'In progress', tone: 'bg-amber-50 text-amber-700' },
  RESOLVED: { label: 'Resolved', tone: 'bg-emerald-50 text-emerald-700' },
  CLOSED: { label: 'Closed', tone: 'bg-emerald-50 text-emerald-700' },
  MERGED: { label: 'Merged', tone: 'bg-gray-100 text-gray-600' },
};

/** A ticket's status as a chip: its words and colours. Unknown ones read as written. */
export const ticketStatus = (status: string): { label: string; tone: string } =>
  STATUS[String(status).toUpperCase()] ?? { label: status || '—', tone: 'bg-gray-100 text-gray-600' };

/**
 * '24 Oct' (with ', 10:12' when `withTime`) from Helpdesk's IST text, read off the text
 * itself so the browser's own time zone never moves a ticket to another day.
 */
export const ticketWhen = (iso: string | null, withTime = false): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(iso ?? ''));
  if (!m) return '—';
  const day = `${Number(m[3])} ${MONTH_SHORT[Number(m[2]) - 1] ?? m[2]}`;
  return withTime ? `${day}, ${m[4]}:${m[5]}` : day;
};

/** Who wrote a message, in the words Helpdesk's hand-over note gives for each sender. */
export const messageFrom = (from: EwsTicketMessage['from']): string =>
  from === 'employee' ? 'Employee'
    : from === 'other' ? 'Other'
      : from === 'assistant' ? 'CP Assistant'
        : from === 'support_team' ? 'Support team'
          : from === 'system' ? 'System'
            : 'Unknown';

const CHANNEL_LABEL: Record<string, string> = { app: 'App chat', whatsapp: 'WhatsApp', email: 'Email', suite: '1Office Suite' };

/** Where a ticket was raised, as people say it. */
export const ticketChannel = (channel: string | null | undefined): string =>
  (channel && CHANNEL_LABEL[channel.toLowerCase()]) || (channel ? channel : '—');

/** "'2 tickets in Sep 2026'" for the window's title. */
export const ticketsTitle = (count: number, ym: string): string =>
  `${count} ticket${count === 1 ? '' : 's'} in ${shortMonth(ym)} ${ym.slice(0, 4)}`;
